import React, { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, useLocation, useParams, Navigate } from 'react-router-dom';
import { Container, LinearProgress } from '@mui/material';
import {
  LegacyRedirect,
  ProductSettingsRoute,
  ProductShellLayout,
  isMoveDetailSegment,
  isPortalAccessSettled,
  isProductRouteMounted,
  useConfigServiceAdapter,
  usePortalProductsState,
  useProductsFailed,
  useProductsLoaded,
} from '@hopdrive/feature-portal-products';
import { ValetOrdersTab, ValetOrdersTabErrorBoundary } from '@hopdrive/feature-order-index';

import siteConfig from '@config';
import { useCustomerConfig, useCustomerConfigLoaded, useRooftop, useUser } from '@store';

import ProtectedRoute from '@components/ProtectedRoute';
import TabErrorBoundary from '@components/TabErrorBoundary';
import EmbedOrderIndexAdapter from '@features/orderIndex/EmbedOrderIndexAdapter';
import { PARTS_DELIVERY_ACTIONS } from '@features/partsDelivery/resolvePartsDeliveryActions';
import { shouldFetchInboundReturns } from '@features/partsDelivery/shouldFetchInboundReturns';
import { useGetPartsReturnsInboundCountQuery } from '@features/partsDelivery/queries/getPartsReturnsInboundCount.query.generated';
import { readGuestPortalEnabled } from '@features/guestRides/guestPortal';
import { resolveGuestRidesActions } from '@features/guestRides/resolveGuestRidesActions';
import { resolveMobileServiceActions } from '@features/mobileService/resolveMobileServiceActions';
import {
  hasValetTab,
  mobileServiceGatedTabs,
  resolveAppraisalsTabs,
  resolveGuestRidesTabs,
  resolveMobileServiceTabs,
  resolvePartsDeliveryTabs,
  resolveValetActions,
  resolveValetTabs,
  unmountedTabs,
  valetGatedTabs,
} from '@hopdrive/feature-portal-products';
import { EMBED_LEGACY_REDIRECTS } from '@features/products/embedLegacyRedirects';
import { withProductTabs } from '@features/products/withProductTabs';
import { withSearchParams } from '@features/products/withSearchParams';
import { withActionsSearch } from '@features/products/withActionsSearch';
import { resolveEmbedValetActions } from '@features/products/resolveEmbedValetActions';
import { resolveOrdersPresentation } from '@features/products/resolveOrdersPresentation';
import { isDriversPageAvailable } from '@features/drivers/pages/driversPageAvailable';
import type { ReportingConfig } from '@features/reporting/types';
import { appendSearch } from '@utils/appendSearch';
import { getDashboardKnobs, getReadOnly, readHiddenButtons } from '@utils/embedSettings';
import { LEGACY_PRODUCT_REDIRECTS, PRODUCT_ROUTES, importMovesPath, movePath, planMovesPath, productRootPath, ridesharePath } from '@features/products/productRoutes';

/** The Drivers list's path, the route below and Manage Drivers' target. */
const DRIVERS_PATH = '/drivers';

// Lazy-load route components for code splitting
const DriverDetailsPage = lazy(() => import('@features/drivers/pages/DriverDetailsPage'));
const DriversPage = lazy(() => import('@features/drivers/pages/DriversPage'));
const MoveDetailsPage = lazy(() => import('@features/moves/pages/MoveDetailsPage'));
const MoveImportPage = lazy(() => import('@features/moveImport/pages/MoveImportPage'));
const MovePlannerPage = lazy(() => import('@features/moveCreation/pages/MovePlannerPage'));
const MSAppointmentDetailsPage = lazy(() => import('@features/appointments/pages/AppointmentDetails'));
const MSRunDetailPage = lazy(() => import('@features/mobileService/pages/RunDetailPage'));
const RidehailDetailsPage = lazy(() => import('@features/ridehails/pages/RidehailDetailsPage'));
const SchedulePage = lazy(() => import('@features/schedule/pages/SchedulePage'));
const PDRunDetailPage = lazy(() => import('@features/partsDelivery/pages/RunDetailPage'));
const AppraisalDetailsPage = lazy(() => import('@features/appraisals/pages/AppraisalDetailsPage'));

// Valet's tab surfaces, lazy like the pages.
const TeamTab = lazy(() => import('@features/moves/tabs/TeamTab'));
const RideSharesTab = lazy(() => import('@features/ridehails/tabs/RideSharesTab'));
const ReportsTab = lazy(() => import('@features/reporting/tabs/ReportsTab'));
const MissingRefReportTab = lazy(() => import('@features/missingRefReport/MissingRefReportTab'));

// Mobile Service's tab surfaces, lazy like the pages.
const MSAppointmentsTab = lazy(() => import('@features/mobileService/tabs/AppointmentsTab'));
const MSRunsTab = lazy(() => import('@features/mobileService/tabs/RunsTab'));
const MSRunsMapTab = lazy(() => import('@features/mobileService/tabs/RunsMapTab'));
const ServiceVehiclesTab = lazy(() => import('@features/mobilityVehicles/ServiceVehiclesTab'));

// Guest Rides' tab surface, lazy like the pages.
const GRRidesTab = lazy(() => import('@features/guestRides/tabs/RidesTab'));

// Appraisals' tab surface, lazy like the pages.
const AppraisalIndexTab = lazy(() => import('@features/appraisals/tabs/AppraisalIndexTab'));

// Parts Delivery's tab surfaces, lazy like the pages.
const PDPendingOrdersTab = lazy(() => import('@features/partsDelivery/tabs/PendingOrdersTab'));
const PDActiveRunsTab = lazy(() => import('@features/partsDelivery/tabs/ActiveRunsTab'));
const PDTemplatesTab = lazy(() => import('@features/partsDelivery/tabs/TemplatesTab'));
const PDReturnsTab = lazy(() => import('@features/partsDelivery/tabs/ReturnsTab'));

// Mobile Service's header actions take no facts, so the definition is one module constant: a
// new identity each render would remount the layout's creation dialogs.
const MOBILE_SERVICE_ACTIONS = resolveMobileServiceActions();

// Guest Rides' content tabs take no facts either; the Settings tab is appended per render
// because whether a settings target exists depends on the session.
const GUEST_RIDES_TABS = resolveGuestRidesTabs(PRODUCT_ROUTES);

// Appraisals' one content tab takes no facts either; its Settings tab is appended per render
// for the same reason.
const APPRAISALS_TABS = resolveAppraisalsTabs(PRODUCT_ROUTES);

// Loading fallback component
const PageLoadingFallback = () => (
  <Container maxWidth="lg" sx={{ mt: 4 }}>
    <LinearProgress />
  </Container>
);


const AppRoutes: React.FC = () => {
  const location = useLocation();
  const customerConfig = useCustomerConfig();
  const showMissingRefReport = customerConfig?.reference_num_report_enabled || false;
  // The Missing Ref Report mounts as a scoped Valet tab when enabled, under the same config gate
  // and label the classic embed gave its own page.
  const missingRefLabel = customerConfig?.reference_num_report_label || 'Missing Ref Report';
  const isCSVImportEnabled = customerConfig?.enable_csv_import || false;
  const reportingDashboardEnabled = (customerConfig?.reporting as ReportingConfig | undefined)?.enabled === true;

  const products = usePortalProductsState();
  const rooftop = useRooftop();
  // A product's pages beside its shell are gated on its visibility; the shell route itself is
  // always registered, and the layout renders the restricted fallback for a hidden product.
  const movesRouteMounted = isProductRouteMounted(products?.entries.moves);
  // Moves' gated tabs decide which of its tab routes mount; memoized so the layout's tab list is stable.
  const valetEntry = products?.entries.moves;
  const valetTabs = React.useMemo(
    () =>
      resolveValetTabs(PRODUCT_ROUTES, valetEntry, {
        missingRefLabel: showMissingRefReport ? missingRefLabel : undefined,
        reportingEnabled: reportingDashboardEnabled,
      }),
    [valetEntry, showMissingRefReport, missingRefLabel, reportingDashboardEnabled],
  );
  // Valet's header actions: Plan Moves, plus Import Moves when the rooftop has the CSV import.
  const valetActions = React.useMemo(() => resolveValetActions(PRODUCT_ROUTES, { csvImportEnabled: isCSVImportEnabled }), [isCSVImportEnabled]);

  // Mobile Service's pages beside its shell are gated on the product's entry being route-visible;
  // the capability is true in the `marketing` state too, so an unentitled rooftop's shell renders
  // the advertise panel instead of tabs, and a hidden one the restricted fallback (decision A10).
  // The Missing Ref Report joins as a scoped tab under the same customer config that gives Valet
  // its own (A11); Service Vehicles is a tab only, with no standalone page (A12).
  const mobileServiceRouteMounted = isProductRouteMounted(products?.entries['mobile-service']);
  const mobileServiceTabs = React.useMemo(
    () => resolveMobileServiceTabs(PRODUCT_ROUTES, { missingRefLabel: showMissingRefReport ? missingRefLabel : undefined }),
    [showMissingRefReport, missingRefLabel],
  );
  // The gated tabs this customer lacks (`unmountedTabs` returns only absent ones); each renders
  // RESTRICTED ACCESS at its own path, beside the shell.
  const restrictedValetTabs = React.useMemo(() => unmountedTabs(valetGatedTabs(PRODUCT_ROUTES), valetTabs), [valetTabs]);
  const restrictedMobileServiceTabs = React.useMemo(
    () => unmountedTabs(mobileServiceGatedTabs(PRODUCT_ROUTES), mobileServiceTabs),
    [mobileServiceTabs],
  );

  // Parts Delivery's pages beside its shell take the same products gate as Mobile Service's. The
  // dealer reads the inbound-returns badge count inside a thin `PartsDeliveryShellLayout`; the embed
  // inlines that layout here so every product block shares one shape (decision S9). Inlining moved
  // the read from product scope to APP scope, so it fired on every route — the hook stays here and
  // unconditional, but `shouldFetchInboundReturns` re-scopes it to `/parts-delivery/*` from the
  // pathname, which is exactly where the dealer's layout mounted it. A rooftop without the product
  // never fires it.
  const partsDeliveryRouteMounted = isProductRouteMounted(products?.entries['parts-delivery']);
  const inboundReturns =
    useGetPartsReturnsInboundCountQuery({
      variables: { customerId: rooftop?.id || 0 },
      skip: !shouldFetchInboundReturns({
        routeMounted: partsDeliveryRouteMounted,
        pathname: location.pathname,
        customerId: rooftop?.id,
      }),
      fetchPolicy: 'cache-and-network',
    }).data?.delivery_returns_aggregate?.aggregate?.count ?? 0;
  const partsDeliveryTabs = React.useMemo(() => resolvePartsDeliveryTabs(PRODUCT_ROUTES, inboundReturns), [inboundReturns]);

  // Guest Rides' shell decides its own access, like every product shell. The dealer reads its two
  // facts in a thin `GuestRidesShellLayout`; the embed inlines that here like the other three (decision S9).
  // Guest Rides is one of the two products with a Settings tab, and the shell would append it
  // INSIDE the layout — after the search was stamped — so `withProductTabs` appends it first
  // and the layout's own `withSettingsTab` no-ops (finding F7). The tab mounts even though the
  // hash session's `dealer` role can read no `guestrides` setting: the panel degrades to its
  // own empty state and a direct `/guest-rides/settings` must not 404 (decision A13).
  const hasSettingsTarget = useConfigServiceAdapter().target !== null;
  const guestRidesActions = React.useMemo(
    () =>
      resolveGuestRidesActions({
        guestPortalEnabled: readGuestPortalEnabled(rooftop),
        rooftop: rooftop ? { id: rooftop.id, name: rooftop.name } : null,
      }),
    [rooftop],
  );

  // Appraisals' detail page takes the same products gate. Appraisals is the second product with a
  // Settings tab, so its tab list goes through `withProductTabs` like Guest Rides' (F7/A13).
  // It has no actions definition at all — no header create menu to withhold — so the route takes
  // no `actions` prop and `readOnly` has nothing to remove here (decision C28).
  const appraisalsRouteMounted = isProductRouteMounted(products?.entries.appraisals);

  // Every embed knob is URL-only, and the shell navigates to a tab's bare `path` on a tab click,
  // so the tab lists carry the current query string (decision S7 / C20).
  const valetShellTabs = React.useMemo(() => withSearchParams(valetTabs, location.search), [valetTabs, location.search]);
  const mobileServiceShellTabs = React.useMemo(
    () => withSearchParams(mobileServiceTabs, location.search),
    [mobileServiceTabs, location.search],
  );
  const partsDeliveryShellTabs = React.useMemo(
    () => withSearchParams(partsDeliveryTabs, location.search),
    [partsDeliveryTabs, location.search],
  );
  const guestRidesEntry = products?.entries['guest-rides'];
  const appraisalsEntry = products?.entries.appraisals;
  const guestRidesShellTabs = React.useMemo(
    () => withProductTabs('guest-rides', guestRidesEntry, GUEST_RIDES_TABS, hasSettingsTarget, location.search),
    [guestRidesEntry, hasSettingsTarget, location.search],
  );
  const appraisalsShellTabs = React.useMemo(
    () => withProductTabs('appraisals', appraisalsEntry, APPRAISALS_TABS, hasSettingsTarget, location.search),
    [appraisalsEntry, hasSettingsTarget, location.search],
  );
  // Valet's header follows the integrator's dashboard knobs (`hiddenButtons`, `showToolbar`), with
  // Manage Drivers wherever the Drivers pages render. The header's links are rendered as plain links
  // by the shell, so their route targets carry the query string the same way the tab paths do.
  const driversAvailable = isDriversPageAvailable(rooftop);
  const valetShellActions = React.useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const shaped = resolveEmbedValetActions(valetActions, {
      hiddenButtons: readHiddenButtons(searchParams),
      showToolbar: getDashboardKnobs(searchParams).showToolbar,
      driversAvailable,
      driversPath: DRIVERS_PATH,
    });
    return shaped && withActionsSearch(shaped, location.search);
  }, [valetActions, driversAvailable, location.search]);
  // The Orders tab follows the same knobs: the view controls, the filters, the metric tiles, the
  // compact accordions, plus refresh and the integrator CSV columns.
  const ordersPresentation = React.useMemo(
    () => resolveOrdersPresentation(getDashboardKnobs(new URLSearchParams(location.search))),
    [location.search],
  );
  const guestRidesShellActions = React.useMemo(
    () => withActionsSearch(guestRidesActions, location.search),
    [guestRidesActions, location.search],
  );
  const mobileServiceShellActions = React.useMemo(
    () => withActionsSearch(MOBILE_SERVICE_ACTIONS, location.search),
    [location.search],
  );
  const partsDeliveryShellActions = React.useMemo(
    () => withActionsSearch(PARTS_DELIVERY_ACTIONS, location.search),
    [location.search],
  );

  // `readOnly` withholds the whole Valet, Mobile Service, Parts Delivery and Guest Rides header
  // action sets (decisions C17, C23, C25–C27, C30): the shell header's creation menu is the single
  // replacement for the per-page create buttons the embed had. Parts Delivery's in-tab controls
  // (C50), Mobile Service's run and appointment controls, the shared Vehicles tab (C52) and the
  // Orders tab's write controls read the knob in-tab (C18, C19, C32, C34); Guest Rides' own copies
  // of the list, grid and card honour the flag and get it from `RidesTab` (C25). Appraisals has no
  // actions definition to withhold (C28), so all five products read the knob the same way here.
  const readOnly = getReadOnly(new URLSearchParams(location.search));

  return (
    <Suspense fallback={<PageLoadingFallback />}>
      <Routes>
        <Route path='/' element={<RedirectToMoves />} />

      {/* The Valet product shell: the tabs the customer has, with the active one as the outlet.
          `readOnly` withholds the whole actions definition, so the header's Plan Moves / New Moves
          menu is absent, the same read the other three products make (decision C30). */}
      <Route
        path={productRootPath('moves')}
        element={
          <ProtectedRoute>
            <ProductShellLayout product='moves' tabs={valetShellTabs} actions={readOnly ? undefined : valetShellActions} />
          </ProtectedRoute>
        }
      >
        <Route element={<TabErrorBoundary />}>
          <Route
            index
            element={
              <EmbedOrderIndexAdapter>
                <ValetOrdersTabErrorBoundary>
                  <ValetOrdersTab presentation={ordersPresentation} />
                </ValetOrdersTabErrorBoundary>
              </EmbedOrderIndexAdapter>
            }
          />
          {hasValetTab(valetTabs, 'team') ? <Route path='team' element={<TeamTab />} /> : null}
          {hasValetTab(valetTabs, 'schedule') ? <Route path='schedule' element={<SchedulePage />} /> : null}
          {hasValetTab(valetTabs, 'rideshares') ? <Route path='rideshares' element={<RideSharesTab />} /> : null}
          {hasValetTab(valetTabs, 'reports') ? <Route path='reports' element={<ReportsTab />} /> : null}
          {hasValetTab(valetTabs, 'missing-ref') ? <Route path='missing-ref' element={<MissingRefReportTab lockedView='moves' />} /> : null}
        </Route>
      </Route>

      {/* A gated Moves tab this customer lacks renders RESTRICTED ACCESS at its own path. */}
      {restrictedValetTabs.map(tab => (
        <Route key={tab.path} path={tab.path} element={<ProtectedRoute available={false} />} />
      ))}

      {/* Valet's pages beside the shell: the details, the rideshare detail, the planner, and the CSV import. */}
      <Route
        path={movePath(':id')}
        element={
          <ProtectedRoute available={movesRouteMounted}>
            <MoveDetailRoute />
          </ProtectedRoute>
        }
      />
      <Route
        path={ridesharePath(':moveId')}
        element={
          <ProtectedRoute available={hasValetTab(valetTabs, 'rideshares')}>
            <RidehailDetailsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={planMovesPath()}
        element={
          <ProtectedRoute requiredFeature='planning' available={movesRouteMounted}>
            <MovePlannerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={importMovesPath()}
        element={
          <ProtectedRoute requiredFeature='planning' available={movesRouteMounted && isCSVImportEnabled}>
            <MoveImportPage />
          </ProtectedRoute>
        }
      />

      {/* The legacy product paths land on their product-prefixed replacements, behind the session check. */}
      {LEGACY_PRODUCT_REDIRECTS.map(redirect => (
        <Route key={redirect.from} path={redirect.from} element={<LegacyRedirectRoute to={redirect.to} />} />
      ))}

      {/* The embed's own legacy paths, the ones the shared table never had (decision S11). */}
      {EMBED_LEGACY_REDIRECTS.map(redirect => (
        <Route key={redirect.from} path={redirect.from} element={<LegacyRedirectRoute to={redirect.to} />} />
      ))}

      {/* The Guest Rides product shell: the Rides board at the product root, then Settings.
          `readOnly` withholds the whole actions definition, so the header's Request Ride and
          Share Code are absent; the board's own per-row actions are gated inside the tab. */}
      <Route
        path='/guest-rides'
        element={
          <ProtectedRoute requiredFeature='guestrides'>
            <ProductShellLayout
              product='guest-rides'
              tabs={guestRidesShellTabs}
              actions={readOnly ? undefined : guestRidesShellActions}
            />
          </ProtectedRoute>
        }
      >
        <Route element={<TabErrorBoundary />}>
          <Route index element={<GRRidesTab />} />
          <Route path='settings' element={<ProductSettingsRoute product='guest-rides' />} />
        </Route>
      </Route>

      {/* The Mobile Service product shell: Appointments at the root, then Runs, the full-bleed
          Map, Service Vehicles, and the Missing Ref Report when the customer enables it. */}
      <Route
        path='/mobile-service'
        element={
          <ProtectedRoute requiredFeature='mobile-service'>
            <ProductShellLayout
              product='mobile-service'
              tabs={mobileServiceShellTabs}
              actions={readOnly ? undefined : mobileServiceShellActions}
            />
          </ProtectedRoute>
        }
      >
        <Route element={<TabErrorBoundary />}>
          <Route index element={<MSAppointmentsTab />} />
          <Route path='runs' element={<MSRunsTab />} />
          <Route path='map' element={<MSRunsMapTab />} />
          <Route path='service-vehicles' element={<ServiceVehiclesTab />} />
          {showMissingRefReport ? (
            <Route path='missing-ref' element={<MissingRefReportTab lockedView='mobileservice' />} />
          ) : null}
        </Route>
      </Route>
      {restrictedMobileServiceTabs.map(tab => (
        <Route
          key={tab.path}
          path={tab.path}
          element={<ProtectedRoute available={false} />}
        />
      ))}

      {/* Mobile Service's pages beside the shell: the run detail and the appointment detail. */}
      <Route
        path='/mobile-service/runs/:id'
        element={
          <ProtectedRoute requiredFeature='mobile-service' available={mobileServiceRouteMounted}>
            <MSRunDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path='/appointments/:id'
        element={
          <ProtectedRoute requiredFeature='mobile-service' available={mobileServiceRouteMounted}>
            <MSAppointmentDetailsPage />
          </ProtectedRoute>
        }
      />

      {/* The Parts Delivery product shell: Pending Orders at the root, then Active Runs,
          Templates, Returns (badged with the inbound count) and the shared Vehicles surface. */}
      <Route
        path='/parts-delivery'
        element={
          <ProtectedRoute requiredFeature='parts-delivery'>
            <ProductShellLayout
              product='parts-delivery'
              tabs={partsDeliveryShellTabs}
              actions={readOnly ? undefined : partsDeliveryShellActions}
            />
          </ProtectedRoute>
        }
      >
        <Route element={<TabErrorBoundary />}>
          <Route index element={<PDPendingOrdersTab />} />
          <Route path='live' element={<PDActiveRunsTab />} />
          <Route path='templates' element={<PDTemplatesTab />} />
          <Route path='returns' element={<PDReturnsTab />} />
          <Route path='service-vehicles' element={<ServiceVehiclesTab />} />
        </Route>
      </Route>

      {/* Parts Delivery's page beside the shell: the run detail. */}
      <Route
        path='/parts-delivery/runs/:id'
        element={
          <ProtectedRoute requiredFeature='parts-delivery' available={partsDeliveryRouteMounted}>
            <PDRunDetailPage />
          </ProtectedRoute>
        }
      />

      {/* The Appraisals product shell: the Appraisal Index at the product root, then Settings.
          No actions definition exists for Appraisals, so the header has no create menu and the
          `readOnly` knob has nothing to withhold at the route; the index's own favorite, appraise
          and export controls and the detail page's save/renew/print stay live (decision C28). */}
      <Route
        path='/appraisals'
        element={
          <ProtectedRoute requiredFeature='appraisals'>
            <ProductShellLayout product='appraisals' tabs={appraisalsShellTabs} />
          </ProtectedRoute>
        }
      >
        <Route element={<TabErrorBoundary />}>
          <Route index element={<AppraisalIndexTab />} />
          <Route path='settings' element={<ProductSettingsRoute product='appraisals' />} />
        </Route>
      </Route>

      {/* Appraisals' page beside the shell: an appraisal's detail. */}
      <Route
        path='/appraisals/:id'
        element={
          <ProtectedRoute requiredFeature='appraisals' available={appraisalsRouteMounted}>
            <AppraisalDetailsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path={DRIVERS_PATH}
        element={
          <ProtectedRoute>
            <DriversPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={`${DRIVERS_PATH}/:id`}
        element={
          <ProtectedRoute>
            <DriverDetailsPage />
          </ProtectedRoute>
        }
      />
      <Route path='*' element={<NoMatch />}></Route>
    </Routes>
    </Suspense>
  );
};

/** The move details page, guarded: a segment under `/moves` that is neither a move id nor a known
 * page (every Moves tab's path and the CSV import are static routes) lands on the Orders tab, query kept. */
function MoveDetailRoute() {
  const { id } = useParams();
  const location = useLocation();
  if (!isMoveDetailSegment(id)) return <Navigate to={appendSearch(productRootPath('moves'), location.search)} replace />;
  return <MoveDetailsPage />;
}

/** A legacy path's redirect behind the session check, so a request with no session shows the
 * authentication notice rather than a progress bar waiting on products that never settle. */
function LegacyRedirectRoute({ to }: { to: string }) {
  return (
    <ProtectedRoute>
      <LegacyRedirect to={to} />
    </ProtectedRoute>
  );
}

function RedirectToMoves() {
  const user = useUser();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const hasHash = Boolean(params.get('hash'));

  // No user and no hash: outside production, land on the embed test harness
  // (public/iframe-test.html) — it's the only place to mint a hash and log
  // in. Only when top-level: a real dealer-site embed (iframe) should show
  // the auth-required message, not the harness.
  // Monorepo builds bake VITE_ENV=prod (not 'production'); the config stack
  // accepts both spellings, so this check must too — `!== 'production'` alone
  // would send prod visitors to the harness.
  const isProdEnv = siteConfig.appEnv === 'prod' || siteConfig.appEnv === 'production';
  const sendToHarness = !user && !hasHash && !isProdEnv && window.self === window.top;

  useEffect(() => {
    if (sendToHarness) {
      // The harness is a static page outside the SPA, so a full navigation
      window.location.replace('/iframe-test.html');
    }
  }, [sendToHarness]);

  // The query string carries the embed's whole configuration, so it rides along on both
  // branches — dropping it here would strip an integrator's settings on the landing hop.
  if (user) {
    return <Navigate to={appendSearch(productRootPath('moves'), location.search)} replace />;
  }

  // If there's a hash parameter, redirect to Moves (auth will happen there)
  if (hasHash) {
    return <Navigate to={appendSearch(productRootPath('moves'), location.search)} replace />;
  }

  if (sendToHarness) {
    return <PageLoadingFallback />;
  }

  // No user and no hash - show auth required message
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '20px',
      textAlign: 'center',
      backgroundColor: '#f5f5f5',
    }}>
      <h1 style={{ color: '#333', marginBottom: '20px' }}>Welcome to the HopDrive Embedded Portal</h1>
      <p style={{ color: '#666', maxWidth: '600px', lineHeight: '1.6' }}>
        This portal requires a valid authentication link. Please check your email for a secure access link,
        or contact your Hopdrive administrator for assistance.
      </p>
      <p style={{ color: '#999', marginTop: '20px', fontSize: '14px' }}>
        Access links contain a secure hash parameter for authentication.
      </p>
    </div>
  );
}

function NoMatch() {
  const location = useLocation();
  const user = useUser();
  const customerConfigLoaded = useCustomerConfigLoaded();
  const productsLoaded = useProductsLoaded();
  const products = usePortalProductsState();
  const productsFailed = useProductsFailed();

  // Hold a bad URL's 404 until the rooftop's config and products settle, with the same loading bar
  // the gated pages show, so a cold load never paints "No match" before the route table settles.
  const settled = isPortalAccessSettled({
    hostReady: customerConfigLoaded,
    productsLoaded,
    productsPresent: products !== undefined,
    productsFailed,
  });
  if (user && !settled) {
    return <LinearProgress />;
  }

  return (
    <div style={{ marginTop: 200 }}>
      <h3>
        No match for <code>{location.pathname}</code>
      </h3>
    </div>
  );
}

export default AppRoutes;
